import { useMemo, useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { track, trackError } from '@/lib/analytics'
import { useChannel } from '@/context/ChannelContext'
import {
  checkChannelBotRights,
  createPoll,
  publishPoll,
  updatePoll,
  fetchPollDetail,
  type ChannelRightsResponse,
  type PollType,
  type CreatePollPayload,
} from '@/api/polls'
import { emitPollEvent } from '@/api/pollEvents'
import { usePollsRef } from '@/api/pollsRef'
import { BotRightsCheckModal } from '@/polls/BotRightsCheckModal'
import { BotRightsResolvedModal } from '@/polls/BotRightsResolvedModal'

/**
 * P1-2 — Poll create `/polls/new`. ТЗ v3.1a §2.2 + 5.7.3 §4.3-error-paths.
 * Один компонент, 4 секции + preview + actions:
 * 1. Тип опроса (immutable после publish) — single/multi/quiz
 * 2. Вопрос (≤300) + варианты (2-10, letters А/Б/В, add/delete)
 * 3. Настройки (4 toggle) — pinToChannel=false hardcoded default (§3.4 v3.1a)
 * 4. Расписание (publish mode + close mode)
 * Actions: Отменить / Сохранить как черновик / Опубликовать в канал.
 * Publish idempotency (§5.7.3): publishing flag → 30s timeout →
 *   409 conflict → редирект на /polls/:pollId + toast «уже опубликован».
 */

const MAX_OPTIONS = 10
const MIN_OPTIONS = 2
const QUESTION_MAX = 300
const DESCRIPTION_MAX = 500 // addendum 2026-07-24, длиннее чем вопрос
const OPTION_MAX = 100 // v3.1a §2.2 codepoints
const LETTERS = ['А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ж', 'З', 'И', 'К']
const PUBLISH_TIMEOUT_MS = 30_000

type PublishMode = 'immediate' | 'scheduled' | 'pulse'
type CloseMode = 'in_1h' | 'in_1d' | 'in_1w' | 'at_date' | 'manual'

interface FormState {
  type: PollType
  question: string
  description: string // addendum 2026-07-24, необязательно
  options: { text: string; isCorrect: boolean }[]
  anonymous: boolean
  showResultsBeforeClose: boolean
  allowRevote: boolean
  pinToChannel: boolean
  publishMode: PublishMode
  closeMode: CloseMode
  // Phase 1 UI не собирает эти поля — держим optional для будущих scheduled/pulse/at_date.
  publishAt?: string
  pulseIntervalDays?: number
  closeAt?: string
  maxOptionsPerUser?: number | null
}

const INITIAL: FormState = {
  type: 'single',
  question: '',
  description: '',
  options: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
  // Виктор 2026-07-28: анонимность выключена по дефолту (юзер сам решает).
  // Так же уже сделано в PollsWizardContext (wizard).
  anonymous: false,
  showResultsBeforeClose: true,
  allowRevote: true,
  pinToChannel: false,
  publishMode: 'immediate',
  closeMode: 'in_1d',
}

// Prefill из ?template=<id>
const TEMPLATE_PREFILL: Partial<Record<string, Partial<FormState>>> = {
  pulse: {
    publishMode: 'pulse',
    question: 'Как вам контент за неделю?',
    options: [
      { text: 'Топ', isCorrect: false },
      { text: 'Норм', isCorrect: false },
      { text: 'Так себе', isCorrect: false },
    ],
    closeMode: 'in_1w',
  },
  topic: {
    question: 'О чём писать в следующем посте?',
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
    ],
  },
  quiz: {
    type: 'quiz',
    question: 'Викторина: ',
    options: [
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
    ],
  },
  blank: {},
}

export function CreatePollPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const templateId = params.get('template')
  // Edit-mode: `?edit=<pollId>` — редактирование существующего draft'а (B PATCH endpoint 2026-07-28).
  const editPollId = params.get('edit')
  // Onboarding entry-point: pollId из виральной подписи (`?ref=<uuid>`).
  const refPollId = params.get('ref')
  // Preselect канала (n=1 branch из welcome-handler'а B).
  const preselectChannelUUID = params.get('channelUUID')
  const { channels, selectChannel, selectedChannel } = useChannel()
  // sourcePoll — из /me/polls-ref, для персонализации banner (question, totalVoters, channelTitle).
  const { data: pollsRef } = usePollsRef()
  const sourcePoll = pollsRef?.sourcePoll ?? null
  const [loadingEdit, setLoadingEdit] = useState<boolean>(!!editPollId)
  const [editLoadError, setEditLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!preselectChannelUUID) return
    const match = channels.find((c) => c.uuid === preselectChannelUUID)
    if (match && selectedChannel?.uuid !== match.uuid) {
      selectChannel(match.uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectChannelUUID, channels.length])

  const [form, setForm] = useState<FormState>(() => ({
    ...INITIAL,
    ...(templateId && TEMPLATE_PREFILL[templateId] ? TEMPLATE_PREFILL[templateId] : {}),
  }))

  // Edit-mode: подгружаем существующий draft на mount и populate FormState.
  // Guard: только `status='draft'` можно редактировать (B PATCH возвращает 409 иначе).
  useEffect(() => {
    if (!editPollId) return
    let cancelled = false
    void (async () => {
      const res = await fetchPollDetail(editPollId)
      if (cancelled) return
      if (!res.data) {
        setEditLoadError('Не удалось загрузить черновик.')
        setLoadingEdit(false)
        return
      }
      const p = res.data.poll
      if (p.status !== 'draft') {
        // Не draft — редактировать нельзя, ведём на results.
        navigate(`/polls/${editPollId}`, { replace: true })
        return
      }
      const opts = res.data.options.map((o) => ({
        text: o.text,
        isCorrect: !!o.isCorrect,
      }))
      setForm((s) => ({
        ...s,
        type: p.type,
        question: p.question,
        description: p.description ?? '',
        options: opts.length ? opts : s.options,
        anonymous: p.anonymous,
        allowRevote: p.allowRevote,
      }))
      // Preselect канала из draft'а (может отличаться от текущего selectedChannel).
      if (p.channelUUID) {
        const match = channels.find((c) => c.uuid === p.channelUUID)
        if (match && selectedChannel?.uuid !== match.uuid) {
          selectChannel(match.uuid)
        }
      }
      setLoadingEdit(false)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPollId])

  useEffect(() => {
    track('poll_create_opened', { templateId: templateId ?? 'none', ref: refPollId ?? null })
    if (refPollId) {
      // B contract §2.6 whitelist funnel event
      emitPollEvent('poll_onboarding_opened', {
        pollId: refPollId,
        meta: {
          source: 'lk',
          entryPath: '/polls/new',
          ref: refPollId,
          templateId: templateId ?? null,
          channelUUID: preselectChannelUUID ?? null,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId])

  useEffect(() => {
    track('poll_type_selected', { type: form.type })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type])

  const questionCharCount = useMemo(() => Array.from(form.question).length, [form.question])
  const descriptionCharCount = useMemo(() => Array.from(form.description).length, [form.description])
  const validOptionsCount = form.options.filter((o) => o.text.trim().length > 0).length
  const hasCorrectAnswer = form.type !== 'quiz' || form.options.some((o) => o.isCorrect && o.text.trim().length > 0)

  const canPublish =
    form.question.trim().length > 0 &&
    validOptionsCount >= MIN_OPTIONS &&
    questionCharCount <= QUESTION_MAX &&
    descriptionCharCount <= DESCRIPTION_MAX &&
    form.options.every((o) => Array.from(o.text).length <= OPTION_MAX) &&
    hasCorrectAnswer

  if (loadingEdit) {
    return (
      <div className="polls-new" style={{ padding: 80, textAlign: 'center', color: 'var(--tg-mute, #8f9eb0)' }}>
        Загружаем черновик…
      </div>
    )
  }
  if (editLoadError) {
    return (
      <div className="polls-new" style={{ padding: 80, textAlign: 'center', color: '#dc3545' }}>
        {editLoadError} <Link to="/polls" style={{ marginLeft: 8, color: 'var(--tg-blue, #0066ff)' }}>Вернуться</Link>
      </div>
    )
  }

  return (
    <div className="polls-new">
      <header className="hero">
        <h1>
          {editPollId ? (
            <>Редактирование <em>черновика</em></>
          ) : (
            <>Новый <em>опрос</em></>
          )}
        </h1>
        <p className="hero-sub">
          {editPollId
            ? 'Меняйте вопрос, варианты и настройки. Опубликуем — только после нажатия «Опубликовать в канал».'
            : 'Бот опубликует опрос в канал. Голосование в один клик через inline-кнопку.'}
        </p>
        {refPollId && (
          <div className="onb-banner" role="status">
            <span className="onb-banner-ic" aria-hidden>✨</span>
            {sourcePoll ? (
              <span>
                Опрос <b>«{sourcePoll.question}»</b> собрал{' '}
                <b>{sourcePoll.totalVoters.toLocaleString('ru-RU')}</b> голос
                {pluralGolos(sourcePoll.totalVoters)} у{' '}
                <b>{sourcePoll.channelTitle ? `«${sourcePoll.channelTitle}»` : 'канала-автора'}</b>.
                Сделай такой же в{' '}
                <b>{selectedChannel?.title ?? 'своём канале'}</b>.
              </span>
            ) : (
              <span>
                Ты пришёл настроить опрос по виральной подписи. Выбери шаблон,
                подставь свой вопрос — бот опубликует в{' '}
                <b>{selectedChannel?.title ?? 'твой канал'}</b> за минуту.
              </span>
            )}
          </div>
        )}
      </header>

      <div className="layout">
        <div className="form-column">
          <TypeSection form={form} setForm={setForm} />
          <QuestionAndOptionsSection
            form={form}
            setForm={setForm}
            questionCharCount={questionCharCount}
            descriptionCharCount={descriptionCharCount}
            hasCorrectAnswer={hasCorrectAnswer}
          />
          <SettingsSection form={form} setForm={setForm} />
          <ScheduleSection form={form} setForm={setForm} />
        </div>
        <PreviewPanel form={form} />
      </div>

      <ActionsBar form={form} canPublish={canPublish} editPollId={editPollId} />

      <PageStyles />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Тип
// ─────────────────────────────────────────────────────────────────────────────
function TypeSection({ form, setForm }: { form: FormState; setForm: (u: (s: FormState) => FormState) => void }) {
  const types: { type: PollType; title: string; desc: string; icon: JSX.Element }[] = [
    {
      type: 'single',
      title: 'Один ответ',
      desc: 'Можно выбрать только один вариант. Самый частый вид опроса.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3.5" fill="currentColor" />
        </svg>
      ),
    },
    {
      type: 'multi',
      title: 'Несколько',
      desc: 'Можно выбрать несколько вариантов. Хорошо для предпочтений.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      ),
    },
    {
      type: 'quiz',
      title: 'Викторина',
      desc: 'Один правильный ответ. Покажем его после голосования.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5M12 16h.01" />
        </svg>
      ),
    },
  ]
  return (
    <section className="sec">
      <SectionHead num={1} title="Тип опроса" sub="Что и как голосуют" />
      <div className="types">
        {types.map((t) => (
          <button
            key={t.type}
            className={`type ${form.type === t.type ? 'on' : ''}`}
            type="button"
            onClick={() =>
              setForm((s) => ({
                ...s,
                type: t.type,
                options:
                  t.type === 'quiz'
                    ? s.options.map((o, i) => ({ ...o, isCorrect: i === 0 })) // первый = правильный по умолчанию
                    : s.options.map((o) => ({ ...o, isCorrect: false })),
              }))
            }
          >
            <div className="type-icon">{t.icon}</div>
            <div className="type-title">{t.title}</div>
            <div className="type-desc">{t.desc}</div>
          </button>
        ))}
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — Вопрос + варианты
// ─────────────────────────────────────────────────────────────────────────────
function QuestionAndOptionsSection({
  form,
  setForm,
  questionCharCount,
  descriptionCharCount,
  hasCorrectAnswer,
}: {
  form: FormState
  setForm: (u: (s: FormState) => FormState) => void
  questionCharCount: number
  descriptionCharCount: number
  hasCorrectAnswer: boolean
}) {
  const addOption = () => {
    if (form.options.length >= MAX_OPTIONS) return
    setForm((s) => ({ ...s, options: [...s.options, { text: '', isCorrect: false }] }))
  }
  const removeOption = (idx: number) => {
    if (form.options.length <= MIN_OPTIONS) return
    setForm((s) => ({ ...s, options: s.options.filter((_, i) => i !== idx) }))
  }
  const updateOption = (idx: number, text: string) => {
    setForm((s) => ({
      ...s,
      options: s.options.map((o, i) => (i === idx ? { ...o, text } : o)),
    }))
  }
  const setCorrect = (idx: number) => {
    setForm((s) => ({
      ...s,
      options: s.options.map((o, i) => ({ ...o, isCorrect: i === idx })),
    }))
  }

  return (
    <section className="sec">
      <SectionHead num={2} title="Вопрос и варианты" sub={`До ${MAX_OPTIONS} вариантов · до ${QUESTION_MAX} символов в вопросе`} />
      <label className="label">
        Вопрос<span className="req">*</span>
      </label>
      <input
        className="input"
        placeholder="Например: какую тему разобрать в следующем посте?"
        value={form.question}
        onChange={(e) => setForm((s) => ({ ...s, question: e.target.value }))}
        maxLength={QUESTION_MAX + 20}
      />
      <div className="input-meta">
        <span>Появится крупно над вариантами</span>
        <span className={questionCharCount > QUESTION_MAX ? 'meta-warn' : ''}>
          {questionCharCount} / {QUESTION_MAX}
        </span>
      </div>

      <label className="label" style={{ marginTop: 20 }}>
        Описание <span className="label-hint">(необязательно)</span>
      </label>
      <textarea
        className="input input-textarea"
        placeholder="Дополнительный контекст, ссылка на источник, пояснение — если одного вопроса мало"
        value={form.description}
        onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
        maxLength={DESCRIPTION_MAX + 40}
        rows={4}
      />
      <div className="input-meta">
        <span>Покажем мелким шрифтом под вопросом</span>
        <span className={descriptionCharCount > DESCRIPTION_MAX ? 'meta-warn' : ''}>
          {descriptionCharCount} / {DESCRIPTION_MAX}
        </span>
      </div>

      <label className="label" style={{ marginTop: 20 }}>
        Варианты ответа<span className="req">*</span>
      </label>
      <div className="input-meta input-meta-options">
        <span>Каждая строка станет отдельной кнопкой под опросом</span>
        <span>Минимум {MIN_OPTIONS}, максимум {MAX_OPTIONS}</span>
      </div>
      <div className="options">
        {form.options.map((opt, i) => (
          <div className={`option-row ${form.type === 'quiz' ? 'is-quiz' : ''}`} key={i}>
            {form.type === 'quiz' && (
              <button
                type="button"
                className={`option-correct ${opt.isCorrect ? 'on' : ''}`}
                title={opt.isCorrect ? 'Правильный ответ' : 'Отметить как правильный'}
                onClick={() => setCorrect(i)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </button>
            )}
            <div className="option-letter">{LETTERS[i] || i + 1}</div>
            <input
              className="input option-input"
              placeholder={`Напишите вариант ${LETTERS[i]}`}
              value={opt.text}
              onChange={(e) => updateOption(i, e.target.value)}
            />
            <button
              type="button"
              className="option-del"
              onClick={() => removeOption(i)}
              disabled={form.options.length <= MIN_OPTIONS}
              title={form.options.length <= MIN_OPTIONS ? `Минимум ${MIN_OPTIONS} варианта` : 'Удалить'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="add-option"
        onClick={addOption}
        disabled={form.options.length >= MAX_OPTIONS}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Добавить вариант
      </button>

      {form.type === 'quiz' && !hasCorrectAnswer && (
        <div className="warn-inline">
          Отметьте один правильный ответ (кликните круг слева от варианта).
        </div>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Настройки (4 toggle)
// ─────────────────────────────────────────────────────────────────────────────
function SettingsSection({ form, setForm }: { form: FormState; setForm: (u: (s: FormState) => FormState) => void }) {
  const toggles = [
    { key: 'anonymous' as const, name: 'Анонимные ответы', desc: 'Никто не увидит, кто как проголосовал. Рекомендуется.' },
    {
      key: 'showResultsBeforeClose' as const,
      name: 'Показывать результаты до закрытия',
      desc: 'Голосующие сразу видят проценты. Если выключить — покажем только после закрытия.',
    },
    {
      key: 'allowRevote' as const,
      name: 'Можно сменить голос',
      desc: 'Пользователь может перевыбрать вариант пока опрос идёт.',
    },
    {
      key: 'pinToChannel' as const,
      name: 'Закрепить пост в канале',
      // v3.1a §3.4 — hardcoded default false в Phase 1; предупреждение о рисках
      desc: 'В Phase 1 закрепление отключено — сначала соберём безопасный pin-restore lifecycle (§3.4).',
      disabled: true,
    },
  ]
  return (
    <section className="sec">
      <SectionHead num={3} title="Настройки" sub="Анонимность, видимость результатов, закрытие" />
      <div className="toggles">
        {toggles.map((t) => (
          <label key={t.key} className={`toggle-row ${t.disabled ? 'disabled' : ''}`}>
            <div>
              <p className="toggle-name">{t.name}</p>
              <p className="toggle-desc">{t.desc}</p>
            </div>
            <span className={`toggle ${form[t.key] ? 'on' : ''}`}>
              <input
                type="checkbox"
                checked={form[t.key]}
                disabled={t.disabled}
                onChange={(e) => setForm((s) => ({ ...s, [t.key]: e.target.checked }))}
              />
              <span className="thumb" />
            </span>
          </label>
        ))}
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 4 — Расписание
// ─────────────────────────────────────────────────────────────────────────────
function ScheduleSection({ form, setForm }: { form: FormState; setForm: (u: (s: FormState) => FormState) => void }) {
  // Phase 1: показываем только рабочие варианты. По расписанию / pulse /
  // at_date требуют date-picker'ы и pulseIntervalDays input — Phase 2.
  // Скрыты в UI, backend их всё ещё принимает (не удаляем из типа CreatePollPayload).
  return (
    <section className="sec">
      <SectionHead num={4} title="Расписание" sub="Когда публиковать и когда закрывать" />
      <label className="label">Опубликовать</label>
      <div className="seg">
        {[
          ['immediate', 'Сразу'],
        ].map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            className={form.publishMode === mode ? 'on' : ''}
            onClick={() => setForm((s) => ({ ...s, publishMode: mode as PublishMode }))}
          >
            {label}
          </button>
        ))}
      </div>

      <label className="label" style={{ marginTop: 18 }}>
        Закрыть голосование
      </label>
      <div className="seg">
        {[
          ['in_1h', 'Через час'],
          ['in_1d', 'Через сутки'],
          ['in_1w', 'Через неделю'],
          ['manual', 'Вручную'],
        ].map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            className={form.closeMode === mode ? 'on' : ''}
            onClick={() => setForm((s) => ({ ...s, closeMode: mode as CloseMode }))}
          >
            {label}
          </button>
        ))}
      </div>
      {form.closeMode !== 'manual' && (
        <p className="input-meta" style={{ marginTop: 8 }}>
          <span>{describeCloseAt(form.closeMode)}</span>
        </p>
      )}
    </section>
  )
}

function describeCloseAt(mode: CloseMode): string {
  if (mode === 'in_1h') return 'Закроется через час · потом покажем итоговые проценты.'
  if (mode === 'in_1d') return 'Закроется через сутки · потом покажем итоговые проценты.'
  if (mode === 'in_1w') return 'Закроется через неделю · потом покажем итоговые проценты.'
  if (mode === 'at_date') return 'Выберите конкретную дату закрытия — календарь появится после публикации.'
  return ''
}

// ─────────────────────────────────────────────────────────────────────────────
// Preview panel
// ─────────────────────────────────────────────────────────────────────────────
function PreviewPanel({ form }: { form: FormState }) {
  const typeLabel = form.type === 'multi' ? 'Опрос · несколько ответов' : form.type === 'quiz' ? 'Викторина · 1 правильный' : 'Опрос · один ответ'
  return (
    <aside className="preview">
      <p className="preview-label">Превью · как увидят в канале</p>
      <div className="phone">
        <div className="phone-head">
          <div className="phone-ch-avatar">КК</div>
          <div className="phone-ch-title">Ваш канал</div>
        </div>
        <div className="poll-card">
          <div className="poll-eyebrow">{typeLabel}</div>
          <p className="poll-q">{form.question || 'Ваш вопрос появится здесь'}</p>
          {form.description && <p className="poll-desc">{form.description}</p>}
          <div className="poll-opts">
            {form.options.map((o, i) => (
              <div className={`poll-opt ${form.type === 'quiz' && o.isCorrect ? 'correct-hint' : ''}`} key={i}>
                <span className="bullet" />
                {o.text || `Вариант ${LETTERS[i]}`}
              </div>
            ))}
          </div>
          <div className="poll-foot">
            <span>0 голосов · {form.anonymous ? 'анонимно' : 'открыто'}</span>
            <span>{closingLabel(form.closeMode)}</span>
          </div>
        </div>
      </div>
      <p className="preview-note">
        В MAX нет встроенных опросов в каналах — мы публикуем карточку через бота. Голосование по тапу на вариант; бот обновляет проценты прямо в этом же сообщении.
      </p>
    </aside>
  )
}

function errorMessageForUpdate(err: { code: string; error?: string }): string {
  switch (err.code) {
    case 'FORBIDDEN': return 'Только автор опроса может его редактировать.'
    case 'NOT_FOUND': return 'Опрос не найден. Возможно, он был удалён.'
    case 'VALIDATION': return err.error ? `Ошибка валидации: ${err.error}` : 'Не удалось сохранить: проверьте поля.'
    case 'NETWORK': return 'Ошибка сети или сервера. Попробуйте ещё раз.'
    default: return 'Не удалось сохранить черновик.'
  }
}

function pluralGolos(n: number): string {
  const abs = Math.abs(n) % 100
  const n1 = abs % 10
  if (abs > 10 && abs < 20) return 'ов'
  if (n1 > 1 && n1 < 5) return 'а'
  if (n1 === 1) return ''
  return 'ов'
}

function closingLabel(mode: CloseMode): string {
  if (mode === 'in_1h') return 'Закрыть через час'
  if (mode === 'in_1d') return 'Закрыть завтра'
  if (mode === 'in_1w') return 'Закрыть через неделю'
  if (mode === 'at_date') return 'Закрыть в дату'
  return 'Закрыть вручную'
}

// ─────────────────────────────────────────────────────────────────────────────
// Actions — footer bar с publish/draft/cancel + idempotency logic
// ─────────────────────────────────────────────────────────────────────────────
function ActionsBar({ form, canPublish, editPollId }: { form: FormState; canPublish: boolean; editPollId: string | null }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const refPollId = params.get('ref')
  const { selectedChannel } = useChannel()
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRightsReadyModal, setShowRightsReadyModal] = useState(false)
  const [botRightsModal, setBotRightsModal] = useState<{ open: boolean; seed: ChannelRightsResponse | null }>({
    open: false,
    seed: null,
  })
  const timeoutRef = useRef<number | null>(null)
  const createAttemptIdRef = useRef<string>(newAttemptId())
  const isEdit = !!editPollId

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const buildPayload = (): CreatePollPayload => {
    const description = form.description.trim()
    return {
      channelUUID: selectedChannel?.uuid ?? 'mock-channel',
      type: form.type,
      question: form.question.trim(),
      ...(description ? { description } : {}),
      options: form.options
        .filter((o) => o.text.trim())
        .map((o) => ({ text: o.text.trim(), ...(form.type === 'quiz' ? { isCorrect: o.isCorrect } : {}) })),
      settings: {
        anonymous: form.anonymous,
        showResultsBeforeClose: form.showResultsBeforeClose,
        allowRevote: form.allowRevote,
        pinToChannel: form.pinToChannel,
        maxOptionsPerUser: form.type === 'multi' ? form.maxOptionsPerUser ?? null : null,
      },
      schedule: {
        publishMode: form.publishMode,
        ...(form.publishAt ? { publishAt: form.publishAt } : {}),
        ...(form.pulseIntervalDays ? { pulseIntervalDays: form.pulseIntervalDays } : {}),
        closeMode: form.closeMode,
        ...(form.closeAt ? { closeAt: form.closeAt } : {}),
      },
      createAttemptId: createAttemptIdRef.current,
    }
  }

  const handleSaveDraft = async () => {
    if (publishing) return
    setError(null)
    if (isEdit) {
      // Edit-mode: PATCH существующий draft. channelUUID/type immutable — B их проигнорирует.
      const payload = buildPayload()
      const { channelUUID: _c, type: _t, createAttemptId: _ca, ...updateBody } = payload
      void _c; void _t; void _ca
      const res = await updatePoll(editPollId!, updateBody)
      if ('code' in res) {
        if (res.code === 'NOT_DRAFT') {
          // Race: пока редактировали, другая вкладка / auto опубликовала.
          track('poll_edit_conflict', { level: 'warn', pollId: editPollId })
          navigate(`/polls/${editPollId}?conflict=1`, { replace: true })
          return
        }
        setError(errorMessageForUpdate(res))
        trackError(new Error(`updatePoll ${res.code}`), 'poll-save-draft-edit')
        return
      }
      track('poll_saved_draft', { pollId: res.pollId, edit: true })
      queryClient.invalidateQueries({ queryKey: ['polls-list'] })
      navigate('/polls?filter=draft')
      return
    }
    const res = await createPoll(buildPayload())
    if (!res) {
      setError('Не удалось сохранить черновик. Попробуйте ещё раз.')
      trackError(new Error('createPoll returned null'), 'poll-save-draft')
      return
    }
    track('poll_saved_draft', { pollId: res.pollId })
    // Инвалидируем polls-list cache — новый draft появится в списке сразу
    // без F5 (react-query иначе отдаёт stale data по staleTime).
    queryClient.invalidateQueries({ queryKey: ['polls-list'] })
    navigate('/polls?filter=draft')
  }

  const handlePublish = async () => {
    if (publishing || !canPublish) return
    setPublishing(true)
    setError(null)
    setShowRightsReadyModal(false)
    if (!selectedChannel?.uuid) {
      setPublishing(false)
      setError('Сначала выберите канал для публикации.')
      return
    }
    const rights = await checkChannelBotRights(selectedChannel.uuid)
    if (rights && rights.missing.length > 0) {
      setPublishing(false)
      setBotRightsModal({ open: true, seed: rights })
      return
    }
    // §5.7.3 idempotency: 30s таймаут кнопки. Если бэк не ответил — отпускаем
    // UI, при следующем клике `createAttemptId` тот же → CAS-INSERT либо
    // проходит первый раз, либо возвращает существующий Poll (idempotency-retry
    // §4.4.4). Юзер публикует один раз — бэк отдаёт одинаковый результат.
    timeoutRef.current = window.setTimeout(() => {
      setPublishing(false)
      setError('Бэк не ответил за 30 секунд. Можно попробовать ещё раз — дубля не будет (idempotency).')
    }, PUBLISH_TIMEOUT_MS)

    // Edit-mode: сначала PATCH существующий draft, потом POST /publish.
    // Не-edit: POST / (create draft) + POST /publish (as before).
    let pollIdToPublish: string
    if (isEdit) {
      const payload = buildPayload()
      const { channelUUID: _c, type: _t, createAttemptId: _ca, ...updateBody } = payload
      void _c; void _t; void _ca
      const patched = await updatePoll(editPollId!, updateBody)
      if ('code' in patched) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        setPublishing(false)
        if (patched.code === 'NOT_DRAFT') {
          track('poll_edit_conflict', { level: 'warn', pollId: editPollId })
          navigate(`/polls/${editPollId}?conflict=1`, { replace: true })
          return
        }
        setError(errorMessageForUpdate(patched))
        trackError(new Error(`updatePoll ${patched.code}`), 'poll-publish-edit-patch')
        return
      }
      pollIdToPublish = patched.pollId
    } else {
      const created = await createPoll(buildPayload())
      if (!created) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        setPublishing(false)
        setError('Не удалось создать опрос. Попробуйте ещё раз.')
        trackError(new Error('createPoll returned null'), 'poll-publish-create')
        return
      }
      pollIdToPublish = created.pollId
    }

    const published = await publishPoll(pollIdToPublish)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (!published) {
      setPublishing(false)
      setError('Не удалось опубликовать опрос. Попробуйте ещё раз.')
      trackError(new Error('publishPoll returned null'), 'poll-publish-live')
      return
    }
    if ('code' in published) {
      setPublishing(false)
      if (published.code === 'BOT_MISSING_RIGHTS') {
        setBotRightsModal({ open: true, seed: null })
        return
      }
      return
    }
    if (published.conflict) {
      track('poll_publish_conflict', { level: 'warn', pollId: published.pollId })
      // §5.7.3: 409 → редирект на results + toast «уже опубликован»
      navigate(`/polls/${published.pollId}?conflict=1`)
      return
    }
    if (refPollId) {
      emitPollEvent('poll_onboarding_step_completed', {
        pollId: published.pollId,
        meta: {
          source: 'lk',
          step: 'published',
          ref: refPollId,
          channelUUID: selectedChannel?.uuid ?? null,
          type: form.type,
        },
      })
    }
    track('poll_published', {
      pollId: published.pollId,
      type: form.type,
      optionsCount: form.options.filter((o) => o.text.trim()).length,
      publishMode: form.publishMode,
      closeMode: form.closeMode,
      anonymous: form.anonymous,
      pinToChannel: form.pinToChannel,
    })
    navigate(`/polls/${published.pollId}`)
  }

  return (
    <div className="actions">
      <Link to="/polls" className="btn-link">
        ← Отменить
      </Link>
      <button className="btn btn-ghost" type="button" onClick={handleSaveDraft} disabled={publishing}>
        {isEdit ? 'Сохранить изменения' : 'Сохранить как черновик'}
      </button>
      <div className="actions-spacer" />
      {error && <span className="error-inline">{error}</span>}
      <button
        className="btn btn-primary"
        type="button"
        onClick={handlePublish}
        disabled={!canPublish || publishing}
        aria-busy={publishing}
      >
        {publishing ? (
          <>
            <span className="spinner" /> Публикуем…
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
            </svg>
            Опубликовать в канал
          </>
        )}
      </button>
      {botRightsModal.open && selectedChannel?.uuid && (
        <BotRightsCheckModal
          channelUUID={selectedChannel.uuid}
          channelTitle={selectedChannel.title}
          seedRights={botRightsModal.seed}
          onCancel={() => setBotRightsModal({ open: false, seed: null })}
          onSatisfied={() => {
            setBotRightsModal({ open: false, seed: null })
            setShowRightsReadyModal(true)
          }}
        />
      )}
      {showRightsReadyModal && (
        <BotRightsResolvedModal onDone={() => setShowRightsReadyModal(false)} />
      )}
    </div>
  )
}

function newAttemptId(): string {
  // ULID-ish (v3.1a §4.4): 26 chars, timestamp + random
  const ts = Date.now().toString(36).padStart(9, '0')
  const rand = Math.random().toString(36).slice(2, 12).padStart(10, '0')
  return `create-${ts}-${rand}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function SectionHead({ num, title, sub }: { num: number; title: string; sub: string }) {
  return (
    <header className="sec-head">
      <div className="sec-num">{num}</div>
      <div>
        <h2 className="sec-title">{title}</h2>
        <p className="sec-sub">{sub}</p>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
function PageStyles() {
  return (
    <style>{`
      .polls-new { max-width: 1160px; margin: 0 auto; padding-bottom: 100px; }
      .hero { margin-bottom: 24px; }
      .hero h1 {
        font-weight: 800; font-size: 32px; letter-spacing: -0.02em;
        color: var(--tg-ink, #111); margin: 0 0 6px;
      }
      .hero h1 em { font-style: italic; font-weight: 400; color: var(--tg-blue, #0066ff); }
      .hero-sub { font-size: 14.5px; color: var(--tg-mute, #8f9eb0); margin: 0; }
      .onb-banner {
        display: flex; align-items: center; gap: 12px;
        margin-top: 14px; padding: 12px 16px;
        background: linear-gradient(90deg, rgba(0,102,255,0.08), rgba(0,102,255,0.02));
        border: 1px solid var(--tg-blue-soft, rgba(0,128,255,0.24));
        border-radius: 12px; font-size: 13.5px;
        color: var(--tg-ink-2, #333); line-height: 1.5;
      }
      .onb-banner b { color: var(--tg-blue-press, #0052cc); font-weight: 700; }
      .onb-banner-ic { font-size: 18px; flex-shrink: 0; }

      .layout { display: grid; grid-template-columns: 1fr 380px; gap: 28px; align-items: start; }
      @media (max-width: 1000px) { .layout { grid-template-columns: 1fr; } }
      .form-column { display: flex; flex-direction: column; gap: 16px; }

      .sec {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px; padding: 20px 22px 22px;
      }
      .sec-head { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; }
      .sec-num {
        width: 28px; height: 28px; border-radius: 8px;
        background: var(--tg-blue-bg, #ebf2ff);
        color: var(--tg-blue-press, #0052cc);
        display: flex; align-items: center; justify-content: center;
        font-weight: 700; font-size: 13px;
      }
      .sec-title { font-weight: 700; font-size: 16px; color: var(--tg-ink, #111); margin: 0; }
      .sec-sub { font-size: 12.5px; color: var(--tg-mute, #8f9eb0); margin: 2px 0 0; }

      .types { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
      @media (max-width: 700px) { .types { grid-template-columns: 1fr; } }
      .type {
        border: 1.5px solid var(--tg-line-2, #f0f0f4);
        border-radius: 12px; padding: 14px;
        text-align: left; cursor: pointer;
        background: var(--tg-bg, #fafafd);
        font-family: inherit;
      }
      .type:hover { border-color: var(--tg-blue-soft, rgba(0,128,255,0.4)); }
      .type.on {
        border-color: var(--tg-blue, #0066ff);
        background: var(--tg-blue-bg, #ebf2ff);
      }
      .type-icon {
        width: 36px; height: 36px; border-radius: 10px;
        background: white; display: flex; align-items: center; justify-content: center;
        margin-bottom: 10px;
      }
      .type-icon svg { width: 20px; height: 20px; color: var(--tg-blue, #0066ff); }
      .type-title { font-weight: 700; font-size: 15px; color: var(--tg-ink, #111); margin-bottom: 4px; }
      .type-desc { font-size: 12px; color: var(--tg-mute, #8f9eb0); line-height: 1.4; }

      .label {
        display: block; font-weight: 600; font-size: 13px;
        color: var(--tg-ink-2, #333); margin-bottom: 6px;
      }
      .label .req { color: #dc3545; margin-left: 2px; }
      .label-hint { color: var(--tg-mute, #8f9eb0); font-weight: 400; font-size: 12px; margin-left: 6px; }
      .input-textarea { resize: vertical; min-height: 96px; font-family: inherit; line-height: 1.45; }
      .input {
        width: 100%; padding: 10px 12px;
        border: 1px solid var(--tg-line-2, #f0f0f4); border-radius: 10px;
        font-family: inherit; font-size: 14px; color: var(--tg-ink, #111);
        background: var(--tg-surface, #fff); box-sizing: border-box;
      }
      .input:focus { outline: none; border-color: var(--tg-blue, #0066ff); }
      .input-meta {
        display: flex; justify-content: space-between;
        font-size: 12px; color: var(--tg-mute, #8f9eb0); margin-top: 6px;
      }
      .input-meta-options { margin: -2px 0 10px; }
      .input-meta .meta-warn { color: #dc3545; font-weight: 600; }

      .options { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
      .option-row {
        display: grid; grid-template-columns: 26px minmax(0, 1fr) auto; gap: 10px; align-items: center;
      }
      .option-row.is-quiz { grid-template-columns: auto 26px minmax(0, 1fr) auto; }
      .option-correct {
        width: 24px; height: 24px; border-radius: 50%;
        border: 2px solid var(--tg-line, #e8e8ec);
        background: transparent; cursor: pointer; padding: 0;
        display: flex; align-items: center; justify-content: center;
      }
      .option-correct svg { width: 14px; height: 14px; color: transparent; }
      .option-correct.on { border-color: var(--tg-up, #17a06a); background: var(--tg-up, #17a06a); }
      .option-correct.on svg { color: white; }
      .option-letter {
        width: 26px; height: 26px; border-radius: 7px;
        background: var(--tg-blue-bg, #ebf2ff);
        color: var(--tg-blue-press, #0052cc);
        display: flex; align-items: center; justify-content: center;
        font-weight: 700; font-size: 12px;
      }
      .option-input { border-radius: 8px; }
      .option-input::placeholder { color: var(--tg-mute, #8f9eb0); }
      .option-del {
        width: 34px; height: 34px; border-radius: 8px;
        background: transparent; border: 1px solid transparent;
        color: var(--tg-mute, #8f9eb0); cursor: pointer;
        display: flex; align-items: center; justify-content: center;
      }
      .option-del svg { width: 15px; height: 15px; }
      .option-del:hover:not(:disabled) { background: #fff8e1; color: #dc3545; border-color: #f0d580; }
      .option-del:disabled { cursor: not-allowed; opacity: 0.3; }
      .add-option {
        margin-top: 10px; padding: 8px 12px;
        background: transparent; border: 1.5px dashed var(--tg-line, #e8e8ec);
        border-radius: 10px; cursor: pointer;
        display: inline-flex; align-items: center; gap: 6px;
        color: var(--tg-mute, #8f9eb0); font-family: inherit; font-size: 13px; font-weight: 600;
      }
      .add-option:hover:not(:disabled) { border-color: var(--tg-blue-soft, rgba(0,128,255,0.4)); color: var(--tg-blue, #0066ff); }
      .add-option:disabled { cursor: not-allowed; opacity: 0.4; }
      .add-option svg { width: 13px; height: 13px; }
      .warn-inline {
        margin-top: 10px; padding: 8px 12px;
        background: #fff8e1; color: #8b6100; border-radius: 8px;
        font-size: 12.5px;
      }

      .toggles { display: flex; flex-direction: column; gap: 12px; }
      .toggle-row {
        display: grid; grid-template-columns: 1fr auto; gap: 12px;
        align-items: center; padding: 12px 14px;
        background: var(--tg-bg, #fafafd); border-radius: 10px;
      }
      .toggle-row.disabled { opacity: 0.65; }
      .toggle-name { font-weight: 600; font-size: 14px; color: var(--tg-ink, #111); margin: 0; }
      .toggle-desc { font-size: 12.5px; color: var(--tg-mute, #8f9eb0); margin: 4px 0 0; line-height: 1.4; }
      .toggle {
        display: inline-flex; align-items: center;
        width: 40px; height: 22px; border-radius: 999px;
        background: var(--tg-line, #e8e8ec);
        position: relative; cursor: pointer;
        transition: background 150ms;
      }
      .toggle.on { background: var(--tg-blue, #0066ff); }
      .toggle input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
      .toggle .thumb {
        width: 18px; height: 18px; border-radius: 50%;
        background: white; position: absolute;
        top: 2px; left: 2px;
        transition: transform 150ms;
        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
      }
      .toggle.on .thumb { transform: translateX(18px); }

      .seg {
        display: inline-flex; gap: 2px; padding: 3px;
        background: var(--tg-surface-2, #f5f5f9); border-radius: 10px;
        flex-wrap: wrap;
      }
      .seg button {
        padding: 7px 12px; border: 0;
        background: transparent; cursor: pointer;
        font-family: inherit; font-weight: 600; font-size: 13px;
        color: var(--tg-mute, #8f9eb0); border-radius: 7px;
      }
      .seg button:hover { color: var(--tg-ink-2, #333); }
      .seg button.on { background: white; color: var(--tg-ink, #111); box-shadow: 0 1px 3px rgba(0,0,0,0.06); }

      /* Preview */
      .preview { position: sticky; top: 84px; }
      .preview-label {
        font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
        color: var(--tg-mute, #8f9eb0); text-transform: uppercase;
        margin: 0 0 12px;
      }
      .phone {
        background: #1a1d2e; border-radius: 18px; padding: 16px;
        color: #e4e6f2; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.35);
      }
      .phone-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
      .phone-ch-avatar {
        width: 30px; height: 30px; border-radius: 50%;
        background: linear-gradient(135deg, #4d9fff, #7c3aed);
        color: white; font-weight: 700; font-size: 11px;
        display: flex; align-items: center; justify-content: center;
      }
      .phone-ch-title { font-weight: 600; font-size: 13px; }
      .poll-card { background: rgba(255,255,255,0.06); border-radius: 8px 16px 16px 16px; padding: 14px; }
      .poll-eyebrow {
        font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
        color: #7c8db3; text-transform: uppercase; margin-bottom: 6px;
      }
      .poll-q { font-size: 15px; font-weight: 700; margin: 0 0 8px; line-height: 1.35; }
      .poll-desc { font-size: 12.5px; color: rgba(228,230,242,0.75); margin: 0 0 12px; line-height: 1.45; white-space: pre-wrap; }
      .poll-opts { display: flex; flex-direction: column; gap: 8px; }
      .poll-opt {
        display: flex; align-items: center; gap: 10px;
        padding: 10px 12px; border: 1px solid rgba(255,255,255,0.12); border-radius: 10px;
        font-size: 13px;
      }
      .poll-opt.correct-hint { border-color: rgba(77,211,154,0.5); }
      .bullet {
        width: 16px; height: 16px; border-radius: 50%;
        border: 1.5px solid rgba(255,255,255,0.3); flex-shrink: 0;
      }
      .poll-foot {
        display: flex; justify-content: space-between;
        margin-top: 12px; font-size: 11px; color: #7c8db3;
      }
      .preview-note {
        margin-top: 14px; font-size: 12.5px; color: var(--tg-mute, #8f9eb0);
        line-height: 1.5;
      }

      .actions {
        position: fixed; bottom: 0; left: var(--tg-sidebar-w, 240px); right: 0;
        background: rgba(255,255,255,0.92);
        backdrop-filter: blur(12px);
        border-top: 1px solid var(--tg-line-2, #f0f0f4);
        box-shadow: 0 -8px 24px rgba(17, 17, 17, 0.04);
        padding: 14px 32px;
        display: flex; align-items: center; gap: 12px;
        z-index: 5;
        box-sizing: border-box;
      }
      @media (max-width: 900px) { .actions { left: 0; } }
      .actions-spacer { flex: 1; }
      .btn-link {
        color: var(--tg-mute, #8f9eb0);
        text-decoration: none; font-size: 13.5px; font-weight: 600;
      }
      .btn-link:hover { color: var(--tg-ink-2, #333); }
      .btn {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 10px 16px; border-radius: 10px;
        font-family: inherit; font-weight: 600; font-size: 13.5px;
        cursor: pointer; border: 1px solid transparent;
      }
      .btn:disabled { opacity: 0.55; cursor: not-allowed; }
      .btn-ghost { background: white; border-color: var(--tg-line-2, #f0f0f4); color: var(--tg-ink-2, #333); }
      .btn-ghost:hover:not(:disabled) { background: var(--tg-surface-2, #f5f5f9); }
      .btn-primary { background: var(--tg-blue, #0066ff); color: white; }
      .btn-primary:hover:not(:disabled) { background: var(--tg-blue-hover, #0052cc); }
      .btn-primary svg { width: 14px; height: 14px; }
      .spinner {
        width: 14px; height: 14px; border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.4); border-top-color: white;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      .error-inline {
        font-size: 12.5px; color: #dc3545; margin-right: 8px;
        max-width: 320px; line-height: 1.4;
      }
      @media (max-width: 720px) {
        .polls-new { padding-bottom: 154px; }
        .preview { position: static; }
        .actions {
          padding: 12px 16px;
          gap: 8px 10px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          grid-template-areas:
            "cancel cancel"
            "draft publish"
            "error error";
          align-items: stretch;
        }
        .actions-spacer { display: none; }
        .btn-link {
          grid-area: cancel;
          text-align: left;
          min-height: 20px;
          display: inline-flex;
          align-items: center;
          font-size: 12.5px;
        }
        .btn {
          min-width: 0;
          justify-content: center;
          white-space: normal;
          line-height: 1.2;
          text-align: center;
          min-height: 48px;
        }
        .btn-ghost { grid-area: draft; }
        .btn-primary { grid-area: publish; }
        .error-inline {
          grid-area: error;
          max-width: none;
          margin-right: 0;
        }
      }
    `}</style>
  )
}
